import classNames from 'classnames';
import React, {
  useState,
  useEffect,
  useRef,
  useContext,
} from 'react';
import get from 'lodash.get';
import LazyLoad from 'react-lazyload';

import loadScript from 'lib/loadScript';
import htmlSetIframeLoadingAttributes from 'lib/htmlSetIframeLoadingAttributes';
import { htmlSetIframeDataSrcAttributes } from 'lib/htmlSetIframeDataSrcAttributes';
import { htmlSetIframeTitle } from 'lib/htmlSetIframeTitle';
import { ArticleContext } from 'lib/ContextTypes';
import Breakpoints from 'lib/Breakpoints';
import { FacebookEmbed } from './FacebookEmbed';
import { ReportUnmounting } from '../../ReportUnmounting';

/**
 * IframelyEmbed component renders an iframe-based embed widget.
 * @param {object} props - The component props.
 * @param {import('../../../../lib/graphqlTypeDefs.mjs').Presentation} props.presentation - Presentation-related data for the embed.
 * @param {import('../../../../lib/graphqlTypeDefs.mjs').Widget} props.widget - Widget data containing embed properties.
 */
export const IframelyEmbed = (props) => {
  const { presentation, widget } = props;

  const iframeWrapper = useRef(null);
  const [lastReportedHeight, setLastReportedHeight] = useState(0);
  const [isMobileOrTabletBreakpoint, setIsMobileOrTabletBreakpoint] = useState(true);
  const defaultPlaceHolderHeight = isMobileOrTabletBreakpoint ? 100 : 500;

  const articleContext = useContext(ArticleContext);
  const { subType } = articleContext;

  useEffect(() => {
    // Iframely's embed.js script should be loaded once for all embeds
    loadScript('https://iframe.nbcnews.com/embed.js');

    setIsMobileOrTabletBreakpoint(Breakpoints.isSorM());
  }, []);

  /**
   * Handles the unmounting of the LazyLoad component by capturing the current height of the iframe wrapper.
   */
  const onLazyLoadUnmounting = () => {
    const height = iframeWrapper?.current?.offsetHeight;


    if (height) {
      setLastReportedHeight(height);
    }
  };

  const siteName = get(widget, 'properties.iframely-embed-site', '');
  const siteNameLowerCase = siteName.toLowerCase();
  const headline = get(widget, 'properties.headline', '');

  /*
    Facebook embeds do not handle responsive behavior correctly.
    To avoid cutting off the embed or causing horizontal overflow,
    we use the existing Ramen Facebook embed with responsive sizing set.
  */
  if (siteNameLowerCase === 'facebook') {
    const reshapedEmbedObject = {
      ...widget,
      properties: {
        'facebookplus-post-url': widget.fallbackUrl,
      },
    };

    return (
      <FacebookEmbed
        presentation={presentation}
        widget={reshapedEmbedObject}
        facebookPostType="post"
      />
    );
  }

  // Iframely uses an iframe to wrap all social embeds.
  // The iframe is injected into a container for rendering.
  const { baseline: iframelyEmbedHTML = '', fallbackUrl } = widget;

  let iframelyEmbed = htmlSetIframeLoadingAttributes(iframelyEmbedHTML, 'lazy');
  iframelyEmbed = htmlSetIframeDataSrcAttributes(iframelyEmbed, fallbackUrl);
  iframelyEmbed = htmlSetIframeTitle(iframelyEmbed, siteName, headline);

  // Deep links (e.g., immediately scroll into view) require an element id
  const shouldEnableDatavizDeepLink = (fallbackUrl || '').match('https://dataviz.nbcnews.com');
  const id = shouldEnableDatavizDeepLink ? fallbackUrl.split('/').slice(-2)[0] : null;
  const isEmbedTimeline = (fallbackUrl || '').match('/embedded-pkg/timeline');

  const isLiveBlog = subType === 'liveBlog';

  const renderEmbedMarkup = (
    <div
      ref={iframeWrapper}
      /* eslint-disable-next-line react/no-danger */
      dangerouslySetInnerHTML={{ __html: iframelyEmbed }}
    />
  );

  const placeholder = (
    <div
      className="embed-widget__placeholder"
      data-testid="iframely-placeholder"
    />
  );

  return (
    <div
      id={id}
      data-testid="iframely-wrapper"
      className={classNames(
        'embed-widget__use-presentation',
        `embed-widget__iframely-${siteNameLowerCase}`,
        { 'embed-widget__iframely-timeline': isEmbedTimeline },
      )}
      style={lastReportedHeight ? { '--iframe-height': `${lastReportedHeight}px` } : undefined}
    >
      {isLiveBlog ? (
        <LazyLoad
          unmountIfInvisible
          offset={defaultPlaceHolderHeight}
          placeholder={placeholder}
        >
          <ReportUnmounting onUnmount={onLazyLoadUnmounting}>
            {renderEmbedMarkup}
          </ReportUnmounting>
        </LazyLoad>
      ) : (
        renderEmbedMarkup
      )}
    </div>
  );
};
